/* eslint-disable */
import React from "react";
import { createPortal } from "react-dom";
import { HiX } from "react-icons/hi";
import { FaAnglesLeft, FaAnglesRight } from "react-icons/fa6";
import Links from "./components/Links";
import { useAuth } from "contexts/AuthContext";
import RoleSwitcher from "components/role/RoleSwitcher";
import ThemeSelector from "components/ui/ThemeSelector";
import Tooltip from "components/ui/Tooltip";
import wasaaLogo from "assets/wasaa/wasaa-logo-horizontal.svg";
import wasaaLogoDark from "assets/wasaa/wasaa-logo-horizontal-on-dark.svg";

import routes, { routeAllowed } from "routes.js";

/**
 * xl and up: Kuest AdminSidebar, a plain 200px column of ghost links beside the content.
 * Below xl: a Kuest drawer (side sheet, 24rem from sm, bg-black/80 overlay) with the
 * role switcher, the nav as grouped cards and a Dark Mode card. Esc closes it and focus
 * returns to the menu button.
 */
const Sidebar = ({ open, onClose, collapsed, onToggleCollapse }) => {
  const { isPartner } = useAuth();
  const visibleRoutes = routes.filter((route) => routeAllowed(route, isPartner));
  const sheetRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const id = requestAnimationFrame(() => sheetRef.current?.focus());
    function onKey(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener("keydown", onKey);
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, [open, onClose]);

  return (
    <>
      {/* Desktop column */}
      <aside className="hidden min-w-0 xl:sticky xl:top-[84px] xl:block xl:max-h-[calc(100vh-100px)] xl:self-start xl:overflow-y-auto">
        <nav aria-label="Portal">
          <ul className="flex flex-col gap-0.5">
            <Links routes={visibleRoutes} collapsed={collapsed} isPartner={isPartner} />
          </ul>
        </nav>
        <div className="mx-3 my-3 h-px bg-border" aria-hidden="true" />
        <Tooltip content={collapsed ? "Expand sidebar" : undefined} side="right">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`flex h-9 w-full items-center gap-2 rounded-sm px-4 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${
              collapsed ? "justify-center px-0" : ""
            }`}
          >
            {collapsed ? <FaAnglesRight /> : <FaAnglesLeft />}
            <span className={collapsed ? "hidden" : ""}>Collapse</span>
          </button>
        </Tooltip>
      </aside>

      {/* Mobile / tablet drawer */}
      {createPortal(
        <div className={`xl:hidden ${open ? "" : "pointer-events-none"}`} aria-hidden={open ? undefined : "true"} inert={!open}>
          <div
            className={`fixed inset-0 z-[60] bg-black/80 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`}
            onClick={onClose}
          />
          <div
            ref={sheetRef}
            role={open ? "dialog" : undefined}
            aria-modal={open ? "true" : undefined}
            aria-label="Menu"
            tabIndex={-1}
            className={`fixed inset-y-0 left-0 z-[61] flex w-[85vw] max-w-sm flex-col bg-background shadow-lg outline-none transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] sm:w-96 sm:max-w-none ${
              open ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-4">
              <img src={wasaaLogo} alt="Wasaa Markets" className="h-7 w-auto dark:hidden" />
              <img src={wasaaLogoDark} alt="Wasaa Markets" className="hidden h-7 w-auto dark:block" />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close menu"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-accent text-foreground opacity-70 transition-opacity hover:opacity-100"
              >
                <HiX className="h-4 w-4" />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-4 pb-6 pt-2">
              <RoleSwitcher variant="sidebar" />
              <nav aria-label="Portal">
                <ul className="flex flex-col gap-4">
                  <Links routes={visibleRoutes} isPartner={isPartner} variant="drawer" onNavigate={onClose} />
                </ul>
              </nav>
              <div className="flex items-center justify-between gap-3 rounded-2xl border border-border px-4 py-3 text-sm font-semibold">
                <span>Dark Mode</span>
                <ThemeSelector />
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default Sidebar;
